<template>
    <CustomMenu
        v-model="isOpenPopup"
        hideTransitionSlider
    >
        <template #activator>
            <div
                class="font-sans-rubik flex items-center gap-x-3.5 cursor-pointer"
                :class="[ mini ? 'justify-center items-center' : 'px-3' ]"
                @click="onOpenCloseAccountMenu"
            >
                <UserImage :image="currentUser.image" />

                <div v-if="!mini">
                    <div class="mb-1 max-w-33.75 text-xs text-gray-370 dark:text-white truncate">
                        {{ userName }}
                    </div>

                    <div class="text-xxxs tracking-[1%] max-w-33.75 text-gray-200 dark:text-gray-250 truncate">
                        {{ currentUser.email }}
                    </div>
                </div>
            </div>
        </template>

        <template #dropdown>
            <div
                class="font-sans-rubik w-53.25 absolute left-14 -bottom-11 rounded-1.25 bg-white dark:bg-blue-800 border border-white-500 dark:border-gray-760 shadow-activeMenu dark:shadow-activeMenuDark"
                @click.stop
            >
                <div class="flex items-center gap-x-3.5 pt-2.5 pb-3.5 px-3.75 border-b-1 border-white-500 dark:border-gray-760">
                    <UserImage :image="currentUser.image" />

                    <div>
                        <div class="mb-1 max-w-33.75 truncate text-xs text-gray-370 dark:text-white">
                            {{ userName }}
                        </div>

                        <div class="max-w-33.75 truncate text-xxxs text-gray-250 dark:text-gray-250">
                            {{ currentUser.email }}
                        </div>
                    </div>
                </div>

                <div class="m-0.5">
                    <template
                        v-for="route in menu"
                        :key="route.id"
                    >
                        <CustomRouterLink
                            class="px-4"
                            :class="menuStyle(route)"
                            :style="customStyle(route)"
                            :route="route"
                            :simpleRote="!route.subMenu && !!route.routeName"
                            @click.stop="onOpenSubMenu(route)"
                        >
                            <div
                                class="font-sans-rubik flex items-center justify-between"
                                :class="[route.isOpenSub && 'mb-2.5']"
                            >
                                <div class="flex items-center gap-x-2">
                                    <div class="w-6 dark:text-gray-200">
                                        <component :is="route.icon()" />
                                    </div>

                                    <div class="text-xs font-medium dark:font-normal">
                                        {{ route.name }}
                                    </div>
                                </div>

                                <ArrowChevronBottomIcon
                                    v-if="route.subMenu"
                                    class="w-5 h-5 transition-all"
                                    :class="{ 'rotate-180': route.isOpenSub }"
                                />
                            </div>

                            <RoutingSideBarBig
                                :route="route"
                                :checkCurrentLink="checkCurrentLink"
                                wrapperSubMenu="ml-2"
                                @onLinkCLick="onOpenCloseAccountMenu"
                            />
                        </CustomRouterLink>
                    </template>
                </div>

                <div class="flex items-center gap-x-2.5 px-3 py-3.5 border-t-1 border-white-500 dark:border-gray-760">
                    <ThemeToggle v-model:currentTheme="currentTheme" />

                    <div class="text-xxxs font-medium text-gray-200">
                        {{ currentTheme }} theme
                    </div>
                </div>
            </div>
        </template>
    </CustomMenu>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import CustomMenu from '@/components/menu/CustomMenu/CustomMenu.vue'
import CustomRouterLink from '@/components/navigation/CustomRouterLink.vue'
import ArrowChevronBottomIcon from '@/components/icons/arrow/20x20/ArrowChevronBottomIcon.vue'
import RoutingSideBarBig from '@/components/navigation/RoutingSideBar/RoutingSideBarBig.vue'
import UserImage from '@/components/userImage/userImage.vue'
import ThemeToggle from '@/components/buttons/ThemeToggle.vue'

import { useAuthorizationStore } from '@/store/authorizationStore'
import { usePermissionsStore } from '@/store/permissionsStore'

import { SETTINGS_MENU_ROUTES_WORKSPACE_OWNER, SETTINGS_MENU_ROUTES_WITH_BILLING, SETTINGS_MENU_ROUTES_WITHOUT_BILLING } from '@/constants/settingsMenu'
import { NAVIGATION_ACTIONS } from '@/constants/navigationActions'
import { ROUTES } from '@/constants/routes'

defineProps({
    mini: {
        type: Boolean,
        default: false,
    },
})

const authorizationStore = useAuthorizationStore()
const permissionsStore = usePermissionsStore()

const route = useRoute()

const currentTheme = ref('')

const isOpenPopup = ref(false)

const currentUser = computed(() => authorizationStore.user || {})
const isWorkspaceOwner = computed(() => authorizationStore.isWorkspaceOwner)
const workspace = computed(() => authorizationStore.workspace)

const billingEditPermission = computed(() => permissionsStore.permissions.billing.edit)

const menu = ref([])

const userName = computed(() => {
    const { first_name, last_name } = currentUser.value

    return `${first_name || ''} ${last_name || ''}`.trim()
})

function hideWhiteLabelForHR(menuItems) {
    if (!authorizationStore.isHR) {
        return menuItems
    }

    return menuItems.map((item) => (item.subMenu
        ? { ...item, subMenu: item.subMenu.filter((sub) => sub.routeName !== ROUTES.whiteLabel) }
        : item))
}

function setDefaultMenu() {
    if (isWorkspaceOwner.value) {
        menu.value = hideWhiteLabelForHR(SETTINGS_MENU_ROUTES_WORKSPACE_OWNER)
    } else if (billingEditPermission.value) {
        menu.value = SETTINGS_MENU_ROUTES_WITH_BILLING
    } else {
        menu.value = SETTINGS_MENU_ROUTES_WITHOUT_BILLING
    }
}

function menuStyle(route) {
    const { isOpenSub, subMenu } = route

    const active = checkCurrentLink(route, subMenu)

    const classes = ['py-2 rounded-1.25 text-xs transition-all cursor-pointer relative']

    if (!subMenu) {
        classes.push('flex')
    }

    if (isOpenSub) {
        classes.push(
            'dark:hover:bg-transparent',
            active ? 'text-gray-370 dark:text-white' : 'text-gray-370 dark:text-white',
        )
    } else {
        classes.push(
            'hover:bg-menu-purple70 dark:hover:bg-menu-blue770',
            active ? 'border-purple dark:border-transparent text-purple dark:text-purple-140' : 'border-transparent text-gray-370 dark:text-white',
        )
    }

    return classes.join(' ')
}

function customStyle({ isOpenSub, subMenu }) {
    if (subMenu) {
        return { height: isOpenSub ? `${46 + (32 * subMenu.length)}px` : '36px' }
    }
}

function onOpenCloseAccountMenu() {
    isOpenPopup.value = !isOpenPopup.value
}

function onOpenSubMenu(route) {
    const { isOpenSub, subMenu } = route

    if (subMenu) {
        route.isOpenSub = !isOpenSub
    } else {
        onOpenCloseAccountMenu()
    }

    const foundElement = menu.value.find((el) => el.name !== route.name && el.isOpenSub)

    if (foundElement) {
        foundElement.isOpenSub = false
    }

    navigationActionHandler(route)
}

function checkCurrentLink(parent, subMenu) {
    const { routeName, isFirstLoad } = parent

    const currentRef = route.meta.reference || route.name

    if (subMenu) {
        const isActive = subMenu.some((el) => el.routeName === currentRef)
        if (isActive && isFirstLoad) {
            parent.isOpenSub = isActive
            parent.isFirstLoad = false
        }
        return isActive
    }
    return currentRef === routeName
}

function navigationActionHandler(route) {
    if (!route.hasOwnProperty('action')) {
        return
    }

    switch (route.action) {
    case NAVIGATION_ACTIONS.logout:
        logout()
    }
}

function logout() {
    authorizationStore.logout()
}

watch(workspace, (workspace) => {
    if (workspace) {
        setDefaultMenu()
    }
}, { immediate: true })

</script>
